import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../App';
import { api, uploadToSignedUrl } from '../lib/api';
import { companyBase } from '../lib/paths';
import { useAsync } from '../lib/useAsync';
import { Button, Card, DataTable, ErrorBanner, Field, Flash, Input, Modal, Select, Spinner } from '../components/ui';
import { dateTime, humanize } from '../lib/format';

/**
 * Which document kinds can establish something about ownership.
 *
 * Bylaws, employment agreements and indemnification agreements are real formation
 * documents that say nothing about who holds shares, so reading them only spends
 * tokens. Pre-selecting the useful ones makes the common case one click, without
 * preventing anyone from choosing otherwise.
 */
const CAP_TABLE_KINDS = new Set([
  'CHARTER',
  'BOARD_CONSENT',
  'SUBSCRIPTION_AGREEMENT',
  'STOCK_CERTIFICATE',
  'OPTION_AGREEMENT',
  'SAFE_AGREEMENT',
  'NOTE_AGREEMENT',
]);

/** Only PDFs and images can be read; anything else is refused server-side. */
const READABLE = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif']);

const KINDS = [
  'STOCK_CERTIFICATE', 'OPTION_AGREEMENT', 'BOARD_CONSENT', 'SAFE_AGREEMENT', 'NOTE_AGREEMENT',
  'SUBSCRIPTION_AGREEMENT', 'VALUATION_REPORT', 'FORM_83B', 'EXERCISE_NOTICE', 'CHARTER', 'OTHER',
];

export default function Documents() {
  const { companyId, role } = useCompany();
  const [uploading, setUploading] = useState(false);
  const [flash, setFlash] = useState(null);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(() => new Set());
  const [reading, setReading] = useState(false);
  const [editing, setEditing] = useState(null);
  const navigate = useNavigate();

  const list = useAsync(() => api.documents(companyId), [companyId]);
  const canEdit = ['LEGAL', 'ADMIN', 'OWNER'].includes(role);

  const rows = list.data ?? [];
  const readable = rows.filter((r) => READABLE.has(r.content_type));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /** Pre-select the documents that can actually establish ownership. */
  const selectCapTableDocs = () =>
    setSelected(new Set(readable.filter((r) => CAP_TABLE_KINDS.has(r.kind)).map((r) => r.id)));

  /**
   * Read the selected documents as one set.
   *
   * One request rather than one per document, because a cap table cannot be built a
   * document at a time: an issuance needs the id of a share class a different
   * document created, and the same holder appears across several under different
   * spellings. The result is a plan on Review, not a change.
   */
  async function readSelected() {
    setReading(true);
    setError(null);
    try {
      const plan = await api.planFromDocuments(companyId, [...selected]);
      setSelected(new Set());
      const skipped = plan.skipped?.length ? `, ${plan.skipped.length} dropped for lack of a citation` : '';
      setFlash(
        `Read ${selected.size} document${selected.size === 1 ? '' : 's'}: ${plan.steps.length} ` +
          `step${plan.steps.length === 1 ? '' : 's'} queued for your review${skipped}. Nothing has been applied.`
      );
      // Straight to Review: the plan is the point, and leaving the user on this
      // page would make them hunt for it.
      navigate(companyBase(companyId, 'review'));
    } catch (err) {
      setError(err);
    } finally {
      setReading(false);
    }
  }

  async function download(row) {
    setError(null);
    try {
      const { downloadUrl } = await api.documentDownload(companyId, row.id);
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError(err);
    }
  }

  async function remove(row) {
    if (!window.confirm(`Delete "${row.title}"? This cannot be undone from here.`)) return;
    setError(null);
    try {
      await api.deleteDocument(companyId, row.id);
      setFlash(`Deleted ${row.title}.`);
      list.reload();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Documents</h1>
          <p>
            Files go straight from your browser to Google Cloud Storage using a short-lived
            signed URL — the bytes never pass through the API. Nothing in the bucket is
            publicly readable.
            {canEdit ? (
              <>
                {' '}
                Select the formation documents and <strong>Read</strong> them to have the cap
                table entries they establish queued for your review. Nothing is applied until
                you approve it.
              </>
            ) : null}
          </p>
        </div>
        {canEdit ? (
          <div className="page-actions">
            {selected.size > 0 ? (
              <Button variant="primary" loading={reading} onClick={readSelected}>
                Read {selected.size} document{selected.size === 1 ? '' : 's'}
              </Button>
            ) : readable.some((r) => CAP_TABLE_KINDS.has(r.kind)) ? (
              <Button onClick={selectCapTableDocs}>Select formation documents</Button>
            ) : null}
            <Button variant={selected.size > 0 ? 'secondary' : 'primary'} onClick={() => setUploading(true)}>
              Upload document
            </Button>
          </div>
        ) : null}
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />
      <ErrorBanner error={error} />

      {list.loading ? <Spinner /> : list.error ? <ErrorBanner error={list.error} onRetry={list.reload} /> : (
        <Card padded={false}>
          <DataTable
            rowKey={(r) => r.id}
            empty="No documents yet."
            columns={[
              ...(canEdit
                ? [
                    {
                      key: 'select',
                      header: '',
                      render: (r) =>
                        READABLE.has(r.content_type) ? (
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            onChange={() => toggle(r.id)}
                            aria-label={`Select ${r.title}`}
                          />
                        ) : (
                          // Refused server-side anyway; saying so here is kinder than
                          // letting someone select it and get a 415.
                          <span className="muted" title="only PDFs and images can be read">
                            —
                          </span>
                        ),
                    },
                  ]
                : []),
              { key: 'title', header: 'Title', render: (r) => <span className="primary-cell">{r.title}</span> },
              { key: 'kind', header: 'Kind', render: (r) => humanize(r.kind) },
              { key: 'stakeholder', header: 'Stakeholder', render: (r) => r.stakeholder_name ?? '—' },
              { key: 'security', header: 'Security', render: (r) => r.security_label ?? '—' },
              { key: 'size', header: 'Size', align: 'right', numeric: true, render: (r) => formatBytes(r.size_bytes) },
              { key: 'uploaded', header: 'Uploaded', render: (r) => dateTime(r.created_at) },
              { key: 'by', header: 'By', render: (r) => r.uploaded_by_email ?? '—' },
              {
                key: 'actions', header: '',
                render: (r) => (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => download(r)}>Download</Button>
                    {canEdit ? (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => remove(r)}>Delete</Button>
                      </>
                    ) : null}
                  </>
                ),
              },
            ]}
            rows={rows}
          />
        </Card>
      )}

      {uploading ? (
        <UploadModal
          companyId={companyId}
          onClose={() => setUploading(false)}
          onDone={(title) => { setUploading(false); setFlash(`Uploaded ${title}.`); list.reload(); }}
        />
      ) : null}

      {editing ? (
        <EditDocumentModal
          companyId={companyId}
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setFlash('Document updated.'); list.reload(); }}
        />
      ) : null}
    </div>
  );
}

function EditDocumentModal({ companyId, doc, onClose, onSaved }) {
  const [form, setForm] = useState({ title: doc.title, kind: doc.kind });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.updateDocument(companyId, doc.id, form);
      onSaved();
    } catch (err) {
      setError(err);
      setSaving(false);
    }
  }

  return (
    <Modal title="Edit document" onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={saving} onClick={submit}>Save</Button>
      </>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <Field label="Title" required>
          <Input value={form.title} onChange={set('title')} required />
        </Field>
        <Field label="Kind">
          <Select value={form.kind} onChange={set('kind')}>
            {KINDS.map((k) => <option key={k} value={k}>{humanize(k)}</option>)}
          </Select>
        </Field>
      </form>
    </Modal>
  );
}

function UploadModal({ companyId, onClose, onDone }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: '', kind: 'OTHER', stakeholderId: '', securityId: '' });
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);
  const stakeholders = useAsync(() => api.stakeholders(companyId), [companyId]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError(new Error('Choose a file first.'));
      return;
    }
    setError(null);
    try {
      // 1. reserve — records intent and returns a signed PUT URL
      setStage('reserving');
      const reserved = await api.reserveDocument(companyId, {
        title: form.title || file.name,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        kind: form.kind,
        ...(form.stakeholderId ? { stakeholderId: form.stakeholderId } : {}),
      });

      // 2. upload straight to GCS
      setStage('uploading');
      await uploadToSignedUrl(reserved.uploadUrl, file);

      // 3. confirm — the API verifies the object actually landed
      setStage('confirming');
      await api.confirmDocument(companyId, reserved.document.id);

      onDone(form.title || file.name);
    } catch (err) {
      setError(err);
      setStage(null);
    }
  }

  return (
    <Modal title="Upload document" onClose={onClose}
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={Boolean(stage)} onClick={submit}>
          {stage ? `${stage}…` : 'Upload'}
        </Button>
      </>}>
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <Field label="File" required>
          <input ref={fileRef} type="file" className="input" required
            accept=".pdf,.png,.jpg,.jpeg,.csv,.txt,.doc,.docx,.xls,.xlsx" />
        </Field>
        <div className="form-grid">
          <Field label="Title" hint="Defaults to the filename">
            <Input value={form.title} onChange={set('title')} />
          </Field>
          <Field label="Kind">
            <Select value={form.kind} onChange={set('kind')}>
              {KINDS.map((k) => <option key={k} value={k}>{humanize(k)}</option>)}
            </Select>
          </Field>
          <Field label="Attach to stakeholder">
            <Select value={form.stakeholderId} onChange={set('stakeholderId')}>
              <option value="">None</option>
              {(stakeholders.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
        </div>
        <p className="muted" style={{ fontSize: 12 }}>
          Accepted: PDF, images, CSV, text, Word and Excel. Up to 50 MB.
        </p>
      </form>
    </Modal>
  );
}

function formatBytes(bytes) {
  const n = Number(bytes ?? 0);
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}
